import {
	CollectionModel,
	CollectionModelFieldSubFormInstance,
	FormModelFieldSubForm,
	SpanSubFormAdaptor,
} from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n';

export interface SpanListItem {
	instanceId: string;
	title: string;
	spanType: string | null;
	color: string | null;
	isLinkedToSource: boolean;
}

/**
 * MobX state management for the span link modal.
 */
export class SpanLinkModalState {
	@observable targetCollection: CollectionModel | null = null;
	@observable sourceCollection: CollectionModel | null = null;
	@observable sourceSpanInstanceId: string | null = null;
	@observable selectedSpanId: string | null = null;
	@observable createNewSpan: boolean = false;

	constructor() {
		makeObservable(this);
	}

	/**
	 * Determine which spans should be shown in the selection list.
	 *
	 * Logic:
	 * 1. If any span on target already links TO source, show ONLY that span
	 *    (user is updating an existing two-way link)
	 * 2. Otherwise, show all unlinked spans (spanTargetCollectionId is null)
	 */
	@computed get availableSpans(): SpanListItem[] {
		if (!this.targetCollection || !this.sourceCollection) return [];

		const allSpans =
			this.targetCollection?.spanSubForm?.value.map((instance) => new SpanSubFormAdaptor(instance)) ?? [];

		const sourceCollectionId = this.sourceCollection.id;

		// Check if any span already links to source
		const spanLinkingToSource = allSpans.find((span) => span.spanTargetCollectionId === sourceCollectionId);

		let filteredSpans: SpanSubFormAdaptor[];
		if (spanLinkingToSource) {
			// If target has span linking to source, that's the ONLY option
			filteredSpans = [spanLinkingToSource];
		} else {
			// Return all unlinked spans
			filteredSpans = allSpans.filter((span) => !span.spanTargetCollectionId);
		}

		return filteredSpans.map((adaptor) => ({
			instanceId: adaptor.instance.instanceId,
			title: adaptor.instance.formTitle || adaptor.instance.title || Translate(T.SPAN),
			spanType: adaptor.spanTypeField.displayValue,
			color: FormModelFieldSubForm.getSpanColor(adaptor.instance),
			isLinkedToSource: adaptor.spanTargetCollectionId === sourceCollectionId,
			adaptor,
		}));
	}

	/**
	 * Check if the target already has a span linking to source
	 */
	@computed get hasExistingLinkToSource(): boolean {
		return this.availableSpans.some((item) => item.isLinkedToSource);
	}

	@computed get selectedSpan(): SpanListItem | null {
		if (!this.selectedSpanId) return null;
		return this.availableSpans.find((item) => item.instanceId === this.selectedSpanId) ?? null;
	}

	@computed get selectedSpanInstance(): CollectionModelFieldSubFormInstance | null {
		if (!this.selectedSpanId || !this.targetCollection?.spanSubForm) return null;
		return this.targetCollection.spanSubForm.getInstance(this.selectedSpanId);
	}

	@computed get canSubmit(): boolean {
		return this.createNewSpan || this.selectedSpanId != null;
	}

	@action initialize(
		sourceCollection: CollectionModel,
		targetCollection: CollectionModel,
		sourceSpanInstanceId: string
	) {
		this.sourceCollection = sourceCollection;
		this.targetCollection = targetCollection;
		this.sourceSpanInstanceId = sourceSpanInstanceId;
		this.selectedSpanId = null;
		this.createNewSpan = false;

		// Auto-select if there's only one option (existing link to source)
		if (this.hasExistingLinkToSource && this.availableSpans.length === 1) {
			this.selectedSpanId = this.availableSpans[0]!.instanceId;
		}
	}

	@action selectSpan(instanceId: string | null) {
		this.selectedSpanId = instanceId;
		this.createNewSpan = false;
	}

	@action selectCreateNew() {
		this.selectedSpanId = null;
		this.createNewSpan = true;
	}

	@action reset() {
		this.targetCollection = null;
		this.sourceCollection = null;
		this.sourceSpanInstanceId = null;
		this.selectedSpanId = null;
		this.createNewSpan = false;
	}
}
